import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactGA from "react-ga4";
import Navbar from "../../components/Navbar";
import SignInModal from "../../components/SignInModal";
import BLOG_ARTICLES from "./articles";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";

function SuburbThroughputExplorer({ popular = [], slug }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [popularRows] = useState(popular);
  const searchTimerRef = useRef(null);
  const lookedUpRef = useRef(false);


  const fetchThroughput = async (params, pendingLabel) => {
    setLoading(true);
    setSelected({ pendingLabel });
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${window.api_host}/suburb-throughput?${qs}`);
      const data = await res.json();
      setSelected(data);
      if (!lookedUpRef.current) {
        lookedUpRef.current = true;
        ReactGA.event({ category: "Blog", action: "throughput_explorer_lookup", label: slug || "unknown" });
      }
    } catch (_) {
      setSelected({ error: true });
    }
    setLoading(false);
  };

  const handleQueryChange = (q) => {
    setQuery(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      setSearched(false);
      setSearching(false);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    setDropdownOpen(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${window.api_host}/location-search?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        const suburbs = (Array.isArray(data) ? data : []).filter(d => d.type === "suburb").slice(0, 8);
        setResults(suburbs);
      } catch (_) {
        setResults([]);
      }
      setSearching(false);
      setSearched(true);
    }, 350);
  };

  const handleSelect = (item) => {
    setQuery(item.label);
    setDropdownOpen(false);
    fetchThroughput({ suburb_id: item.value }, item.label);
  };

  const handlePopularClick = (row) => {
    setQuery(`${row.suburb}, ${row.state}`);
    if (row.annual_pct != null) {
      setSelected({
        label: `${row.suburb}, ${row.state}`,
        suburb: row.suburb,
        state: row.state,
        annual_throughput_pct: row.annual_pct,
      });
      if (!lookedUpRef.current) {
        lookedUpRef.current = true;
        ReactGA.event({ category: "Blog", action: "throughput_explorer_lookup", label: slug || "unknown" });
      }
    } else {
      fetchThroughput({ suburb: row.suburb, state: row.state }, `${row.suburb}, ${row.state}`);
    }
  };

  const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

  return (
    <div style={{ margin: "2.5rem 0", padding: "1.75rem 1.5rem", background: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: "16px" }}>
      <p style={{ fontSize: "0.85rem", fontWeight: 700, color: BRAND_COLOR, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "1rem", textAlign: "center" }}>
        Throughput by suburb
      </p>
      <div style={{ position: "relative", marginBottom: "1.25rem" }}>
        <input
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
          placeholder="Search any Australian suburb..."
          style={{ width: "100%", padding: "0.75rem 1rem", fontSize: "1rem", border: "1px solid #cbd5e1", borderRadius: "10px", outline: "none", background: "#fff", boxSizing: "border-box" }}
        />
        {dropdownOpen && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #cbd5e1", borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.08)", zIndex: 10, maxHeight: "260px", overflowY: "auto" }}>
            {searching && (
              <div style={{ padding: "0.7rem 1rem", fontSize: "0.95rem", color: "#6b7280", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ display: "inline-block", width: "14px", height: "14px", border: "2px solid #cbd5e1", borderTopColor: BRAND_ACCENT_COLOR, borderRadius: "50%", animation: "vl-spin 0.8s linear infinite" }} />
                Searching suburbs…
              </div>
            )}
            {!searching && searched && results.length === 0 && (
              <div style={{ padding: "0.7rem 1rem", fontSize: "0.95rem", color: "#6b7280" }}>
                No suburbs found
              </div>
            )}
            {!searching && results.map((r, idx) => (
              <div
                key={idx}
                onClick={() => handleSelect(r)}
                style={{ padding: "0.7rem 1rem", cursor: "pointer", fontSize: "0.95rem", borderBottom: idx === results.length - 1 ? "none" : "1px solid #f1f5f9" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                {r.label}
              </div>
            ))}
            <style>{`@keyframes vl-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>

      {selected && !selected.error && (
        <div style={{ background: "#fff", borderRadius: "12px", padding: "1.25rem", border: "1px solid #e5e7eb", textAlign: "center", marginBottom: "1.5rem", minHeight: "130px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          {loading ? (
            <>
              <span
                style={{
                  display: "inline-block",
                  width: "36px",
                  height: "36px",
                  border: "3px solid #e5e7eb",
                  borderTopColor: BRAND_ACCENT_COLOR,
                  borderRadius: "50%",
                  animation: "vl-spin 0.7s linear infinite",
                }}
              />
              <p style={{ margin: "0.75rem 0 0 0", fontSize: "0.9rem", color: "#374151", fontWeight: 600 }}>
                Crunching the numbers for {selected.pendingLabel || "this suburb"}…
              </p>
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.78rem", color: "#6b7280" }}>
                Counting properties and recent sales
              </p>
            </>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#6b7280" }}>{selected.label || `${selected.suburb || ""}${selected.state ? `, ${selected.state}` : ""}`}</p>
              <p style={{ margin: "0.3rem 0 0 0", fontSize: "2.5rem", fontWeight: 800, color: BRAND_ACCENT_COLOR, lineHeight: 1.1 }}>
                {fmtPct(selected.annual_throughput_pct)}
              </p>
              <p style={{ margin: "0.3rem 0 0 0", fontSize: "0.78rem", color: "#6b7280" }}>annualised listing throughput</p>
            </>
          )}
        </div>
      )}
      {selected && selected.error && (
        <div style={{ background: "#fef2f2", borderRadius: "10px", padding: "0.75rem 1rem", color: "#b91c1c", fontSize: "0.9rem", marginBottom: "1.25rem" }}>
          Couldn't load that suburb. Try another.
        </div>
      )}

      {popularRows.length > 0 && (
        <>
          <p style={{ fontSize: "0.78rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>
            Popular suburbs
          </p>
          <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
            {popularRows.map((row, idx) => (
              <div
                key={idx}
                onClick={() => handlePopularClick(row)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.7rem 1rem", cursor: "pointer", borderBottom: idx === popularRows.length - 1 ? "none" : "1px solid #f1f5f9", fontSize: "0.95rem" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <span style={{ color: "#1f2937" }}>{row.suburb}, {row.state}</span>
                <span style={{ fontWeight: 700, color: BRAND_COLOR }}>{fmtPct(row.annual_pct)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <p style={{ fontSize: "0.78rem", color: "#6b7280", textAlign: "center", marginTop: "1rem", marginBottom: 0, lineHeight: 1.5 }}>
        Throughput = annualised sold listings (last 30 days × 12) divided by total properties in the suburb.
      </p>
    </div>
  );
}

function DatabaseValueSlider({ max = 2000, throughput = 0.02, medianPrice = 1_000_000, commissionRate = 0.02, defaultValue, caption, slug }) {
  const [addresses, setAddresses] = useState(typeof defaultValue === "number" ? defaultValue : Math.round(max / 2));
  const firstInteractionRef = useRef(false);
  const settleTimerRef = useRef(null);

  const trackInteraction = (value) => {
    if (!firstInteractionRef.current) {
      firstInteractionRef.current = true;
      ReactGA.event({
        category: "Blog",
        action: "database_slider_first_interaction",
        label: slug || "unknown",
        value,
      });
    }
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      ReactGA.event({
        category: "Blog",
        action: "database_slider_settled",
        label: slug || "unknown",
        value,
      });
    }, 600);
  };

  useEffect(() => () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
  }, []);

  const listingsPerYear = addresses * throughput;
  const commissionPerListing = medianPrice * commissionRate;
  const totalValue = listingsPerYear * commissionPerListing;
  const fmtMoney = (v) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
    return `$${Math.round(v)}`;
  };
  return (
    <div style={{ margin: "2.5rem 0", padding: "1.75rem 1.5rem", background: "linear-gradient(135deg, #f8fafc 0%, #f0fdf4 100%)", border: "2px solid #bbf7d0", borderRadius: "16px" }}>
      <p style={{ fontSize: "0.85rem", fontWeight: 700, color: BRAND_COLOR, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "1rem", textAlign: "center" }}>
        Apply it to your own database
      </p>
      <label style={{ display: "block", fontSize: "0.95rem", color: "#374151", marginBottom: "0.5rem", fontWeight: 600 }}>
        Addresses in your database: <span style={{ color: BRAND_ACCENT_COLOR, fontWeight: 800 }}>{addresses.toLocaleString()}</span>
      </label>
      <input
        type="range"
        min={0}
        max={max}
        step={10}
        value={addresses}
        onChange={(e) => {
          const v = Number(e.target.value);
          setAddresses(v);
          trackInteraction(v);
        }}
        style={{ width: "100%", accentColor: BRAND_ACCENT_COLOR, cursor: "pointer" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>
        <span>0</span>
        <span>{max.toLocaleString()}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1.5rem" }}>
        <div style={{ background: "#fff", borderRadius: "10px", padding: "1rem", textAlign: "center", border: "1px solid #e5e7eb" }}>
          <p style={{ margin: 0, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Sold listings / year</p>
          <p style={{ margin: "0.4rem 0 0 0", fontSize: "1.75rem", fontWeight: 800, color: BRAND_COLOR }}>{Math.round(listingsPerYear).toLocaleString()}</p>
        </div>
        <div style={{ background: "#fff", borderRadius: "10px", padding: "1rem", textAlign: "center", border: "1px solid #e5e7eb" }}>
          <p style={{ margin: 0, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Commission flow / year</p>
          <p style={{ margin: "0.4rem 0 0 0", fontSize: "1.75rem", fontWeight: 800, color: BRAND_ACCENT_COLOR }}>{fmtMoney(totalValue)}</p>
        </div>
      </div>
      <p style={{ fontSize: "0.78rem", color: "#6b7280", textAlign: "center", marginTop: "1rem", marginBottom: 0, lineHeight: 1.5 }}>
        Assumes a {Math.round(throughput * 100)}% annual listing throughput, a median sale price of {fmtMoney(medianPrice)}, and a {(commissionRate * 100).toFixed(1)}% commission rate.
      </p>
      {caption && (
        <p style={{ textAlign: "center", fontSize: "0.85rem", color: "#6b7280", fontStyle: "italic", marginTop: "0.75rem", lineHeight: 1.5, marginBottom: 0 }}>
          {caption}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Product mockups — used by the "no-screenshots" product updates. Each mockup
// is wrapped in a browser-style window so the reader can tell it's a UI render,
// not a real screenshot. Mockups intentionally mirror the production styling
// (brand navy, accent green, Inter, 8px radii, soft shadows).
// ─────────────────────────────────────────────────────────────────────────────
function BrowserWindow({ url, children, dark }) {
  const headerBg = dark ? "#0f172a" : "#f3f4f6";
  const urlBg = dark ? "#1e293b" : "#fff";
  const urlColor = dark ? "#cbd5e1" : "#6b7280";
  const borderColor = dark ? "#1e293b" : "#e5e7eb";
  return (
    <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${borderColor}`, boxShadow: "0 12px 40px rgba(15,23,42,0.18)", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: headerBg, borderBottom: `1px solid ${borderColor}` }}>
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff5f57", display: "inline-block" }} />
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#febc2e", display: "inline-block" }} />
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#28c840", display: "inline-block" }} />
        {url && (
          <div style={{ flex: 1, marginLeft: 10, padding: "5px 12px", background: urlBg, borderRadius: 6, fontSize: 12, color: urlColor, fontFamily: "'SF Mono', Menlo, monospace", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {url}
          </div>
        )}
      </div>
      <div style={{ background: "#f9fafb" }}>{children}</div>
    </div>
  );
}

const NAVY = "#1a3f6b";
const GREEN = "#10b981";

function Avatar({ name, color, size = 32 }) {
  const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color || NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: size * 0.4, flexShrink: 0 }}>{initials}</div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 80 ? "#16a34a" : score >= 60 ? "#0a9fa6" : score >= 40 ? "#e8740c" : "#94a3b8";
  const bg = score >= 80 ? "#dcfce7" : score >= 60 ? "#cffafe" : score >= 40 ? "#fed7aa" : "#e2e8f0";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 999, background: bg, color, fontWeight: 700, fontSize: 12 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />{score}
    </span>
  );
}

function Tag({ text, ai }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: ai ? "#ede9fe" : "#e0f2fe", color: ai ? "#6d28d9" : "#075985", border: `1px solid ${ai ? "#ddd6fe" : "#bae6fd"}` }}>
      {ai && <span style={{ fontSize: 10 }}>✨</span>}{text}
    </span>
  );
}

function MockScorePopup() {
  const signals = [
    { label: "Email opens (last 30d)", v: 92, w: "8 of 8 opened" },
    { label: "Property views", v: 88, w: "Visited 14 times" },
    { label: "Reply sentiment", v: 76, w: "Engaged, asks questions" },
    { label: "Article reads", v: 81, w: "Read 4 of 5 articles" },
    { label: "Ownership address known", v: 100, w: "12 Banksia Ave, Roseville" },
    { label: "Days since last contact", v: 64, w: "5 days ago" },
  ];
  return (
    <div style={{ padding: 24, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 460, padding: "22px 24px", boxShadow: "0 24px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name="Sarah Mitchell" color="#0a9fa6" />
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: 15 }}>Sarah Mitchell</p>
              <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>sarah.m@gmail.com</p>
            </div>
          </div>
          <span style={{ fontSize: 18, color: "#94a3b8", cursor: "pointer" }}>×</span>
        </div>
        <div style={{ margin: "18px 0", textAlign: "center", padding: "16px 0", background: "linear-gradient(135deg,#f0fdf4,#ecfdf5)", borderRadius: 12, border: "1px solid #bbf7d0" }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#166534", letterSpacing: "0.06em", textTransform: "uppercase" }}>Contact Score</p>
          <p style={{ margin: "4px 0 0 0", fontSize: 44, fontWeight: 800, color: "#16a34a", lineHeight: 1 }}>87<span style={{ fontSize: 18, color: "#64748b", fontWeight: 600 }}>/100</span></p>
          <p style={{ margin: "6px 0 0 0", fontSize: 12, color: "#166534", fontWeight: 600 }}>Hot — high listing intent</p>
        </div>
        <p style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 8 }}>What drives this score</p>
        {signals.map((s) => (
          <div key={s.label} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: "#334155", fontWeight: 600 }}>{s.label}</span>
              <span style={{ color: "#64748b" }}>{s.w}</span>
            </div>
            <div style={{ height: 6, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${s.v}%`, height: "100%", background: s.v >= 80 ? "#16a34a" : s.v >= 60 ? "#0a9fa6" : "#e8740c", borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockContactsTab() {
  const rows = [
    { name: "Sarah Mitchell", email: "sarah.m@gmail.com", score: 87, tags: [["Owner-occupier"], ["Curious about subdivision", true]], last: "5d ago", color: "#0a9fa6" },
    { name: "David Nguyen", email: "dnguyen@outlook.com", score: 73, tags: [["VIP"], ["Replies fast", true]], last: "2d ago", color: "#7c3aed" },
    { name: "Priya Sharma", email: "priya.s@gmail.com", score: 64, tags: [["Investor"], ["Asked about rental yields", true]], last: "9d ago", color: "#e8740c" },
    { name: "James O'Connor", email: "james.oc@bigpond.com", score: 52, tags: [["Past client"], ["Renovating", true]], last: "3w ago", color: "#0ea5e9" },
    { name: "Emma Wright", email: "emma.wright@gmail.com", score: 41, tags: [["Newsletter"], ["Browsing only", true]], last: "1mo ago", color: "#16a34a" },
    { name: "Tom Bayliss", email: "tom.b@iinet.com.au", score: 28, tags: [["Cold"]], last: "4mo ago", color: "#94a3b8" },
  ];
  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: 180, background: NAVY, color: "#fff", padding: "16px 0", minHeight: 420 }}>
        <div style={{ padding: "0 18px 18px", fontWeight: 800, fontSize: 14, letterSpacing: "-0.02em" }}>Vendorleads</div>
        {[
          ["📊", "Dashboard"], ["👥", "Contacts", true], ["📥", "AI Inbox"], ["✅", "Review"], ["💬", "Agent Chat"], ["📨", "Campaigns"], ["📱", "QR Codes"], ["📈", "Articles"], ["⚙️", "Settings"],
        ].map(([icon, label, active]) => (
          <div key={label} style={{ padding: "8px 18px", display: "flex", alignItems: "center", gap: 10, fontSize: 13, background: active ? "rgba(255,255,255,0.10)" : "transparent", borderLeft: active ? `3px solid ${GREEN}` : "3px solid transparent", color: active ? "#fff" : "rgba(255,255,255,0.75)" }}>
            <span>{icon}</span>{label}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, padding: "18px 22px", background: "#f9fafb" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <h3 style={{ margin: 0, color: NAVY, fontSize: 18, fontWeight: 800 }}>Contacts</h3>
            <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#64748b" }}>2,418 contacts · sorted by score</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ padding: "5px 10px", fontSize: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, color: "#475569" }}>🔍 Search</span>
            <span style={{ padding: "5px 10px", fontSize: 12, background: GREEN, color: "#fff", borderRadius: 6, fontWeight: 600 }}>+ Add contact</span>
          </div>
        </div>
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 0.6fr 1.8fr 0.7fr", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
            <div>Contact</div><div>Score</div><div>Tags</div><div>Last activity</div>
          </div>
          {rows.map((r, i) => (
            <div key={r.email} style={{ display: "grid", gridTemplateColumns: "1.6fr 0.6fr 1.8fr 0.7fr", padding: "12px 14px", alignItems: "center", borderTop: i ? "1px solid #f1f5f9" : "none", fontSize: 13 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar name={r.name} color={r.color} size={30} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: "#0f172a" }}>{r.name}</p>
                  <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>{r.email}</p>
                </div>
              </div>
              <div><ScoreBadge score={r.score} /></div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{r.tags.map(([t, ai]) => <Tag key={t} text={t} ai={ai} />)}</div>
              <div style={{ color: "#64748b", fontSize: 12 }}>{r.last}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockTagging() {
  return (
    <div style={{ padding: 22, background: "#f9fafb", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <Avatar name="David Nguyen" color="#7c3aed" />
          <div><p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>David Nguyen</p><p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>Roseville, NSW</p></div>
        </div>
        <p style={{ margin: "0 0 6px 0", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Your tags</p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          <Tag text="VIP" /><Tag text="Past client" /><Tag text="Roseville buyer" />
          <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#64748b", border: "1px dashed #cbd5e1", background: "transparent" }}>+ Add tag</span>
        </div>
        <p style={{ margin: "0 0 6px 0", fontSize: 11, fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>✨ AI tags <span style={{ fontWeight: 500, color: "#94a3b8" }}>· from replies & behaviour</span></p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Tag text="Replies fast" ai /><Tag text="Curious about subdivision" ai /><Tag text="Considering selling in 6–12mo" ai /><Tag text="Price sensitive" ai />
        </div>
      </div>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
        <p style={{ margin: "0 0 8px 0", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Why "Considering selling in 6–12mo"</p>
        <div style={{ padding: 12, background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, fontSize: 12, color: "#4c1d95", lineHeight: 1.55, marginBottom: 12 }}>
          <p style={{ margin: 0 }}>Reply on 24 May: <em>"We're not in a rush, but probably want to make a move before our daughter starts uni next year."</em></p>
          <p style={{ margin: "8px 0 0 0" }}>Reply on 11 Apr: <em>"Could you let me know if anything sells on Banksia Ave?"</em></p>
        </div>
        <p style={{ margin: "0 0 6px 0", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Suggested next action</p>
        <div style={{ padding: 10, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 12, color: "#166534" }}>
          Send a comparable-sale alert for Banksia Ave when the next listing goes live.
        </div>
      </div>
    </div>
  );
}

function MockReviewTab() {
  const items = [
    { name: "Sarah Mitchell", color: "#0a9fa6", subject: "Re: Your property update — 12 Banksia Ave", incoming: "Thanks Michael — could you also let me know what's selling around the corner on Wattle St? We've been thinking about it for a while.", draft: "Hi Sarah,\n\nGreat question — Wattle St has been busy lately. The 4-bed at no. 28 sold for $1.92M two weeks ago, and there's a 3-bed currently on the market guiding $1.65M. Happy to put together a quick comparison for your place vs. those sales if that would help — just say the word.\n\nMichael", flagged: false },
    { name: "Priya Sharma", color: "#e8740c", subject: "Re: Rental yields in your suburb", incoming: "What kind of yields are achievable on a 2-bed apartment in this area at the moment?", draft: "Hi Priya,\n\n2-beds in Roseville are currently renting between $720–$820/wk depending on condition and parking, which puts gross yields around 3.4–3.9% on typical purchase prices. Want me to send through a few recent rental comparables?\n\nMichael", flagged: true },
  ];
  return (
    <div style={{ display: "flex", minHeight: 460 }}>
      <div style={{ width: 280, background: "#fff", borderRight: "1px solid #e5e7eb" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, fontSize: 16, color: NAVY, fontWeight: 800 }}>Review queue</h3>
          <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#64748b" }}>12 AI drafts awaiting your approval</p>
        </div>
        {items.map((it, i) => (
          <div key={i} style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", background: i === 0 ? "#f0fdf4" : "#fff", borderLeft: i === 0 ? `3px solid ${GREEN}` : "3px solid transparent" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Avatar name={it.name} color={it.color} size={26} />
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{it.name}</p>
                <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>{it.subject}</p>
              </div>
              {it.flagged && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#fee2e2", color: "#b91c1c", fontWeight: 700 }}>⚑ Flagged</span>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, padding: "18px 22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>From: <strong style={{ color: "#0f172a" }}>Sarah Mitchell</strong> · sarah.m@gmail.com</p>
            <p style={{ margin: "2px 0 0 0", fontSize: 14, color: NAVY, fontWeight: 700 }}>Re: Your property update — 12 Banksia Ave</p>
          </div>
          <ScoreBadge score={87} />
        </div>
        <div style={{ padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#334155", lineHeight: 1.6, marginBottom: 14 }}>{items[0].incoming}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>✨ AI draft reply</span>
          <span style={{ fontSize: 11, color: "#64748b" }}>· Tone: friendly · 92% match to your past replies</span>
        </div>
        <div style={{ padding: 14, background: "#fff", border: `1px solid ${GREEN}`, borderRadius: 10, fontSize: 13, color: "#0f172a", lineHeight: 1.65, whiteSpace: "pre-wrap", marginBottom: 14, boxShadow: "0 4px 14px rgba(16,185,129,0.10)" }}>{items[0].draft}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <span style={{ padding: "8px 16px", background: GREEN, color: "#fff", fontSize: 13, fontWeight: 600, borderRadius: 7 }}>✓ Approve & send</span>
          <span style={{ padding: "8px 16px", background: "#fff", border: "1px solid #e5e7eb", color: "#334155", fontSize: 13, fontWeight: 600, borderRadius: 7 }}>✎ Edit</span>
          <span style={{ padding: "8px 16px", background: "#fff", border: "1px solid #e5e7eb", color: "#64748b", fontSize: 13, fontWeight: 600, borderRadius: 7 }}>↻ Regenerate</span>
          <span style={{ padding: "8px 16px", background: "#fff", border: "1px solid #fecaca", color: "#b91c1c", fontSize: 13, fontWeight: 600, borderRadius: 7 }}>✕ Reject</span>
        </div>
      </div>
    </div>
  );
}

function MockAgentChat() {
  const msgs = [
    { who: "agent", text: "Morning Michael — quick recap: 3 new replies overnight, 2 awaiting your review. Sarah Mitchell's score jumped from 71 to 87 after she clicked through her property update twice." },
    { who: "you", text: "Draft a short market wrap for Roseville this week, plain text, friendly tone. Pull in the two new sales on Banksia Ave." },
    { who: "agent", text: "Done — opened it in Campaigns. Tagged ~640 contacts in Roseville and the surrounding suburbs. Want me to schedule it for Thursday 10am like last time?" },
    { who: "you", text: "Yes. And send a comparable alert to anyone with the 'Banksia Ave' AI tag." },
    { who: "agent", text: "Queued for 12 contacts. I'll skip David Nguyen — he asked to pause updates until July." },
  ];
  return (
    <div style={{ padding: 22, background: "#f9fafb", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 520, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", boxShadow: "0 8px 28px rgba(15,23,42,0.08)" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10, background: "#fff" }}>
          <Avatar name="Michael Jones" color="#0a9fa6" size={34} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Michael Jones <span style={{ fontSize: 11, fontWeight: 500, color: "#16a34a" }}>● online</span></p>
            <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>Your AI associate · michael@yourbrand.agency</p>
          </div>
          <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 999, background: "#ede9fe", color: "#6d28d9", fontWeight: 700 }}>✨ AGENT</span>
        </div>
        <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 10, background: "#f8fafc" }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.who === "you" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "78%", padding: "10px 13px", borderRadius: 12, background: m.who === "you" ? NAVY : "#fff", color: m.who === "you" ? "#fff" : "#0f172a", fontSize: 13, lineHeight: 1.55, border: m.who === "you" ? "none" : "1px solid #e5e7eb", borderBottomRightRadius: m.who === "you" ? 4 : 12, borderBottomLeftRadius: m.who === "you" ? 12 : 4 }}>
                {m.text}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "10px 14px", borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 8, background: "#fff" }}>
          <div style={{ flex: 1, padding: "8px 12px", background: "#f1f5f9", borderRadius: 999, fontSize: 13, color: "#94a3b8" }}>Ask Michael anything…</div>
          <span style={{ width: 32, height: 32, borderRadius: "50%", background: GREEN, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>↑</span>
        </div>
      </div>
    </div>
  );
}

function MockAIInbox() {
  const emails = [
    { from: "Sarah Mitchell", color: "#0a9fa6", subject: "Re: Your property update — 12 Banksia Ave", snippet: "Thanks Michael — could you also let me know what's selling around the corner on Wattle St?…", time: "08:42", unread: true, ai: "replied", score: 87 },
    { from: "Priya Sharma", color: "#e8740c", subject: "Re: Rental yields in your suburb", snippet: "What kind of yields are achievable on a 2-bed apartment in this area at the moment?", time: "07:11", unread: true, ai: "drafted", score: 64 },
    { from: "David Nguyen", color: "#7c3aed", subject: "Pause updates until July please", snippet: "We're away for a couple of months — can you pause updates until 1 July? Cheers.", time: "Yesterday", unread: false, ai: "handled", score: 73 },
    { from: "James O'Connor", color: "#0ea5e9", subject: "Re: Roseville May market wrap", snippet: "Interesting numbers. Any thoughts on whether the apartment market is moving the same way?", time: "Yesterday", unread: false, ai: "drafted", score: 52 },
    { from: "Emma Wright", color: "#16a34a", subject: "Re: Your monthly property update", snippet: "Looks great, thanks!", time: "2d", unread: false, ai: "handled", score: 41 },
  ];
  const aiLabel = { replied: ["Replied", "#dcfce7", "#166534"], drafted: ["Draft ready", "#ede9fe", "#6d28d9"], handled: ["Handled", "#e2e8f0", "#475569"] };
  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: 220, background: "#fff", borderRight: "1px solid #e5e7eb", padding: "16px 0" }}>
        <div style={{ padding: "0 16px 14px", display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar name="Michael Jones" color="#0a9fa6" size={28} />
          <div><p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "#0f172a" }}>AI Inbox</p><p style={{ margin: 0, fontSize: 10, color: "#64748b" }}>michael@yourbrand.agency</p></div>
        </div>
        {[
          ["📥", "Inbox", "12", true], ["✨", "AI drafted", "4"], ["✓", "AI replied", "27"], ["⚑", "Flagged", "1"], ["📤", "Sent", ""], ["🗄", "Archived", ""],
        ].map(([icon, label, count, active]) => (
          <div key={label} style={{ padding: "7px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13, color: active ? NAVY : "#334155", background: active ? "#f0fdf4" : "transparent", borderLeft: active ? `3px solid ${GREEN}` : "3px solid transparent", fontWeight: active ? 700 : 500 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}><span>{icon}</span>{label}</span>
            {count && <span style={{ fontSize: 11, color: "#64748b" }}>{count}</span>}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, background: "#f9fafb" }}>
        <div style={{ padding: "12px 18px", borderBottom: "1px solid #e5e7eb", background: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16, color: NAVY, fontWeight: 800 }}>Inbox</h3>
          <span style={{ fontSize: 11, color: "#64748b" }}>✨ AI handled 27 of 31 conversations this week</span>
        </div>
        {emails.map((e, i) => {
          const [lbl, bg, col] = aiLabel[e.ai];
          return (
            <div key={i} style={{ padding: "12px 18px", borderBottom: "1px solid #f1f5f9", background: e.unread ? "#fff" : "#f9fafb", display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 12, alignItems: "center" }}>
              <Avatar name={e.from} color={e.color} size={32} />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontWeight: e.unread ? 700 : 600, fontSize: 13, color: "#0f172a" }}>{e.from}</span>
                  <ScoreBadge score={e.score} />
                  <span style={{ padding: "1px 7px", fontSize: 10, fontWeight: 700, borderRadius: 999, background: bg, color: col }}>✨ {lbl}</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.subject}</p>
                <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.snippet}</p>
              </div>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{e.time}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QrPattern({ size = 110 }) {
  // Decorative deterministic QR-like pattern. Not a real code.
  const cells = 21;
  const cell = size / cells;
  const rng = (i, j) => ((i * 73856093) ^ (j * 19349663)) >>> 0;
  const isFinder = (i, j) => (i < 7 && j < 7) || (i < 7 && j > cells - 8) || (i > cells - 8 && j < 7);
  const drawFinder = (x, y) => (
    <g key={`f-${x}-${y}`}>
      <rect x={x} y={y} width={cell * 7} height={cell * 7} fill="#0f172a" />
      <rect x={x + cell} y={y + cell} width={cell * 5} height={cell * 5} fill="#fff" />
      <rect x={x + cell * 2} y={y + cell * 2} width={cell * 3} height={cell * 3} fill="#0f172a" />
    </g>
  );
  const dots = [];
  for (let i = 0; i < cells; i++) {
    for (let j = 0; j < cells; j++) {
      if (isFinder(i, j)) continue;
      if ((rng(i, j) % 100) < 48) dots.push(<rect key={`${i}-${j}`} x={j * cell} y={i * cell} width={cell} height={cell} fill="#0f172a" />);
    }
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", background: "#fff" }}>
      {dots}
      {drawFinder(0, 0)}
      {drawFinder((cells - 7) * cell, 0)}
      {drawFinder(0, (cells - 7) * cell)}
    </svg>
  );
}

function MockQrFlyer() {
  const props = [
    { num: "12", street: "Banksia Ave", beds: 4, baths: 2, cars: 2, sel: true },
    { num: "14", street: "Banksia Ave", beds: 3, baths: 2, cars: 1, sel: true },
    { num: "16", street: "Banksia Ave", beds: 4, baths: 3, cars: 2, sel: true },
    { num: "18", street: "Banksia Ave", beds: 3, baths: 1, cars: 1, sel: false },
    { num: "20", street: "Banksia Ave", beds: 5, baths: 3, cars: 2, sel: true },
  ];
  return (
    <div style={{ padding: 22, display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 18, background: "#f9fafb" }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Step 1 — Find a street</p>
        <div style={{ marginTop: 6, padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
          📍 Banksia Ave, Roseville NSW 2069
        </div>
        <p style={{ margin: "16px 0 6px 0", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Step 2 — Pick the properties</p>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          {props.map((p, i) => (
            <div key={i} style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 10, borderTop: i ? "1px solid #f1f5f9" : "none", background: p.sel ? "#f0fdf4" : "#fff" }}>
              <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${p.sel ? GREEN : "#cbd5e1"}`, background: p.sel ? GREEN : "#fff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>{p.sel ? "✓" : ""}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", flex: 1 }}>{p.num} {p.street}</span>
              <span style={{ fontSize: 11, color: "#64748b" }}>🛏 {p.beds} · 🛁 {p.baths} · 🚗 {p.cars}</span>
            </div>
          ))}
        </div>
        <p style={{ margin: "16px 0 0 0", fontSize: 12, color: "#64748b" }}>4 properties selected · each gets its own QR page</p>
        <div style={{ marginTop: 10, padding: "9px 14px", background: GREEN, color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: "center" }}>📄 Generate PDF</div>
      </div>
      <div>
        <p style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "center" }}>Live PDF preview</p>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 18, boxShadow: "0 6px 18px rgba(15,23,42,0.08)" }}>
          <div style={{ textAlign: "center", borderBottom: "1px solid #e5e7eb", paddingBottom: 12, marginBottom: 14 }}>
            <p style={{ margin: 0, fontSize: 10, letterSpacing: "0.12em", color: GREEN, fontWeight: 800 }}>YOUR AGENCY</p>
            <p style={{ margin: "4px 0 0 0", fontSize: 15, fontWeight: 800, color: NAVY }}>What's your home worth?</p>
            <p style={{ margin: "2px 0 0 0", fontSize: 11, color: "#64748b" }}>Scan for a free, instant valuation of <strong>14 Banksia Ave</strong></p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <div style={{ padding: 8, border: `2px solid ${NAVY}`, borderRadius: 8 }}><QrPattern size={130} /></div>
          </div>
          <p style={{ margin: 0, fontSize: 10, textAlign: "center", color: "#64748b" }}>Powered by Vendorleads · Michael Jones · 0400 000 000</p>
        </div>
        <p style={{ margin: "10px 0 0 0", fontSize: 11, color: "#64748b", textAlign: "center", fontStyle: "italic" }}>One personalised page per property in a single printable PDF.</p>
      </div>
    </div>
  );
}

function MockComparablesFinder() {
  const comps = [
    { addr: "8 Wattle St", beds: 4, baths: 2, cars: 2, price: "$1.92M", date: "2 weeks ago", status: "Sold", sel: true },
    { addr: "22 Bayview Rd", beds: 4, baths: 3, cars: 2, price: "$2.05M", date: "1 month ago", status: "Sold", sel: true },
    { addr: "5 Cliff Ave", beds: 4, baths: 2, cars: 1, price: "$1.78M", date: "3 weeks ago", status: "Sold", sel: true },
    { addr: "11 Lindfield St", beds: 4, baths: 2, cars: 2, price: "$1.88M", date: "5 weeks ago", status: "Sold", sel: false },
    { addr: "30 Boundary Rd", beds: 5, baths: 3, cars: 2, price: "$2.25M", date: "6 weeks ago", status: "Sold", sel: false },
    { addr: "3 Wattle St", beds: 4, baths: 2, cars: 2, price: "$1.95M", date: "2 months ago", status: "Sold", sel: true },
  ];
  const selected = comps.filter((c) => c.sel);
  const avg = "$1.93M";
  return (
    <div style={{ padding: 22, background: "#f9fafb", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
      <div>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <p style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Find comparable sales near you</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <div style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12, color: "#0f172a" }}>📍 Roseville, NSW</div>
            <div style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12, color: "#0f172a" }}>🏠 House</div>
            <div style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12, color: "#0f172a" }}>🛏 4 bedrooms</div>
            <div style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12, color: "#0f172a" }}>📆 Last 3 months</div>
          </div>
          <div style={{ padding: "8px 12px", background: NAVY, color: "#fff", fontSize: 12, fontWeight: 600, borderRadius: 7, textAlign: "center" }}>🔍 Find comparables</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {comps.map((c) => (
            <div key={c.addr} style={{ background: c.sel ? "#fff" : "#f1f5f9", border: `1px solid ${c.sel ? "#e5e7eb" : "#e2e8f0"}`, borderRadius: 8, padding: 10, opacity: c.sel ? 1 : 0.55, position: "relative" }}>
              <span style={{ position: "absolute", top: 8, right: 8, width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${c.sel ? GREEN : "#cbd5e1"}`, background: c.sel ? GREEN : "#fff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>{c.sel ? "✓" : ""}</span>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{c.addr}</p>
              <p style={{ margin: "2px 0 6px 0", fontSize: 11, color: "#64748b" }}>🛏 {c.beds} · 🛁 {c.baths} · 🚗 {c.cars}</p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: NAVY }}>{c.price}</p>
              <p style={{ margin: "2px 0 0 0", fontSize: 10, color: "#64748b" }}>{c.status} · {c.date}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div style={{ background: "linear-gradient(135deg,#f0fdf4,#ecfdf5)", border: "1px solid #bbf7d0", borderRadius: 12, padding: 18, position: "sticky", top: 0 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: "#166534", letterSpacing: "0.06em", textTransform: "uppercase" }}>Summary · {selected.length} comparables selected</p>
          <p style={{ margin: "10px 0 0 0", fontSize: 36, fontWeight: 800, color: "#16a34a", lineHeight: 1.1 }}>{avg}</p>
          <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#166534", fontWeight: 600 }}>Avg sale across your selection</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
            <div style={{ background: "#fff", border: "1px solid #d1fae5", padding: "8px 10px", borderRadius: 7 }}>
              <p style={{ margin: 0, fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>Range</p>
              <p style={{ margin: "2px 0 0 0", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>$1.78M – $2.05M</p>
            </div>
            <div style={{ background: "#fff", border: "1px solid #d1fae5", padding: "8px 10px", borderRadius: 7 }}>
              <p style={{ margin: 0, fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>Avg days on market</p>
              <p style={{ margin: "2px 0 0 0", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>27 days</p>
            </div>
            <div style={{ background: "#fff", border: "1px solid #d1fae5", padding: "8px 10px", borderRadius: 7 }}>
              <p style={{ margin: 0, fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>$/sqm (median)</p>
              <p style={{ margin: "2px 0 0 0", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>$10,420</p>
            </div>
            <div style={{ background: "#fff", border: "1px solid #d1fae5", padding: "8px 10px", borderRadius: 7 }}>
              <p style={{ margin: 0, fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>Most common</p>
              <p style={{ margin: "2px 0 0 0", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>4 bed · 2 bath</p>
            </div>
          </div>
          <p style={{ margin: "14px 0 0 0", fontSize: 11, color: "#475569", fontStyle: "italic", lineHeight: 1.5 }}>Deselect any comparable to instantly recompute. Your agent gets a notification every time you use this tool.</p>
        </div>
      </div>
    </div>
  );
}

const PRODUCT_MOCKUPS = {
  scorePopup: { url: "vendorleads.agency/contacts/sarah-mitchell", render: () => <MockScorePopup /> },
  contactsTab: { url: "vendorleads.agency/contacts", render: () => <MockContactsTab /> },
  tagging: { url: "vendorleads.agency/contacts/david-nguyen", render: () => <MockTagging /> },
  reviewTab: { url: "vendorleads.agency/review", render: () => <MockReviewTab /> },
  agentChat: { url: "vendorleads.agency/chat", render: () => <MockAgentChat /> },
  aiInbox: { url: "vendorleads.agency/inbox", render: () => <MockAIInbox /> },
  qrFlyer: { url: "vendorleads.agency/qr-codes/property", render: () => <MockQrFlyer /> },
  comparablesFinder: { url: "yourbrand.vendorleads.com.au/comparables", render: () => <MockComparablesFinder /> },
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = BLOG_ARTICLES[slug];
  const [showSignIn, setShowSignIn] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const articleRef = useRef(null);
  const scrollMilestonesRef = useRef(new Set());

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Scroll depth tracking: 25%, 50%, 75%
  useEffect(() => {
    if (!article || !articleRef.current) return;

    // Scroll-triggered alert email to bayan@vendorleads.com.au.
    // Fires once per page load at each of ALERT_DEPTHS_PCT,
    // provided there's a ?t= short code from a newsletter link.
    // No cross-session or server-side dedup — every fresh visit triggers.
    const ALERT_DEPTHS_PCT = [20, 50, 80];
    const alertedDepths = new Set();
    const sendScrollAlert = (depthPct) => {
      if (alertedDepths.has(depthPct)) return;
      let code = null;
      try { code = new URLSearchParams(window.location.search).get('t'); } catch (_) { /* ignore */ }
      let paramToken = null;
      let campaign = null;
      try { paramToken = localStorage.getItem('param_token'); } catch (_) { /* ignore */ }
      try { campaign = localStorage.getItem('sms_campaign'); } catch (_) { /* ignore */ }
      if (!code && !paramToken) return; // need at least one identifier
      alertedDepths.add(depthPct);
      try {
        const url = `${window.api_host}/notify-blog-scroll`;
        const body = JSON.stringify({
          code,
          param_token: paramToken,
          campaign,
          slug,
          title: article.title || '',
          depth_pct: depthPct,
          source: window.location.href,
        });
        // fetch with text/plain → CORS-simple, no preflight, not classed
        // as a "ping" so tracker blockers don't kill it.
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body,
          keepalive: true,
          mode: 'cors',
          credentials: 'omit',
        }).catch(() => {});
      } catch (_) { /* non-blocking */ }
    };

    const handleScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const articleTop = window.scrollY + rect.top;
      const articleHeight = rect.height;
      const scrollPos = window.scrollY + window.innerHeight - articleTop;
      const pct = Math.min(100, Math.max(0, (scrollPos / articleHeight) * 100));

      [25, 50, 75].forEach((milestone) => {
        if (pct >= milestone && !scrollMilestonesRef.current.has(milestone)) {
          scrollMilestonesRef.current.add(milestone);
          ReactGA.event({
            category: "Blog",
            action: `scroll_depth_${milestone}`,
            label: slug,
          });
        }
      });

      ALERT_DEPTHS_PCT.forEach((depth) => {
        if (pct >= depth) sendScrollAlert(depth);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [article, slug]);

  if (!article) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        <h1 style={{ fontSize: "2rem", color: BRAND_COLOR }}>Article not found</h1>
        <Link to="/" style={{ color: BRAND_ACCENT_COLOR, marginTop: "1rem" }}>Back to home</Link>
      </div>
    );
  }

  const renderInline = (text) => {
    if (typeof text !== "string") return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={idx} style={{ color: "#0f172a", fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  };

  const renderBlock = (block, i) => {
    switch (block.type) {
      case "paragraph":
        return (
          <p key={i} style={{ fontSize: "1.08rem", lineHeight: 1.8, color: "#374151", marginBottom: "1.5rem" }}>
            {renderInline(block.text)}
          </p>
        );
      case "heading":
        return (
          <h2 key={i} style={{ fontSize: "1.5rem", fontWeight: 700, color: BRAND_COLOR, margin: "2.5rem 0 1rem 0", lineHeight: 1.3 }}>
            {block.text}
          </h2>
        );
      case "callout":
        return (
          <blockquote
            key={i}
            style={{
              margin: "2rem 0",
              padding: "1.25rem 1.5rem",
              borderLeft: `4px solid ${BRAND_ACCENT_COLOR}`,
              background: "#f0fdf4",
              borderRadius: "0 8px 8px 0",
              fontSize: "1.05rem",
              lineHeight: 1.7,
              color: "#1f2937",
              fontStyle: "italic",
            }}
          >
            {renderInline(block.text)}
          </blockquote>
        );
      case "image":
        return (
          <figure key={i} style={{ margin: "2rem 0", textAlign: "center" }}>
            <img
              src={block.src}
              alt={block.alt || ""}
              style={{
                width: "100%",
                maxWidth: block.maxWidth || "100%",
                display: "block",
                margin: "0 auto",
                borderRadius: "12px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
              }}
            />
            {block.caption && (
              <figcaption
                style={{
                  marginTop: "0.75rem",
                  fontSize: "0.9rem",
                  color: "#6b7280",
                  fontStyle: "italic",
                  lineHeight: 1.5,
                }}
              >
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      case "badge":
        return (
          <span
            key={i}
            style={{
              display: "inline-block",
              marginTop: "2.5rem",
              padding: "0.25rem 0.85rem",
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#fff",
              background: block.color || "#0a9fa6",
              borderRadius: "999px",
              letterSpacing: "0.03em",
              textTransform: "uppercase",
            }}
          >
            {block.text}
          </span>
        );
      case "cta": {
        const buttonStyle = {
          display: "inline-block",
          padding: "0.85rem 2.5rem",
          background: BRAND_ACCENT_COLOR,
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "1.05rem",
          textDecoration: "none",
          boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
          cursor: "pointer",
        };
        return (
          <div
            key={i}
            style={{
              margin: "3rem 0 2rem 0",
              padding: isMobile ? "2rem 1.5rem" : "2.5rem 3rem",
              background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #134e4a 100%)",
              borderRadius: "16px",
              textAlign: "center",
            }}
          >
            <p style={{ color: "#fff", fontSize: "1.3rem", fontWeight: 700, marginBottom: block.perks && block.perks.length > 0 ? "1rem" : "1.25rem" }}>
              {block.text}
            </p>
            {block.perks && block.perks.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: "center", marginBottom: "1.25rem" }}>
                {block.perks.map((perk, pi) => (
                  <span key={pi} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: "0.4rem 0.85rem", background: "rgba(16,185,129,0.18)", color: "#a7f3d0", border: "1px solid rgba(16,185,129,0.45)", borderRadius: "999px", fontSize: "0.85rem", fontWeight: 600 }}>
                    {perk}
                  </span>
                ))}
              </div>
            )}
            {block.openSignIn ? (
              <button onClick={() => setShowSignIn(true)} style={buttonStyle}>
                {block.buttonText}
              </button>
            ) : (
              <a href={block.buttonUrl} style={buttonStyle}>
                {block.buttonText}
              </a>
            )}
          </div>
        );
      }
      case "diagram":
        return (
          <div key={i} style={{ margin: "2.5rem 0" }}>
            {block.title && (
              <p style={{ fontSize: "0.85rem", fontWeight: 700, color: BRAND_COLOR, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "1rem", textAlign: "center" }}>
                {block.title}
              </p>
            )}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "1.5rem" : "1.5rem", alignItems: "stretch" }}>
              {block.columns.map((col, ci) => (
                <div
                  key={ci}
                  style={{
                    flex: 1,
                    background: col.background || "#f9fafb",
                    border: `2px solid ${col.borderColor || "#e5e7eb"}`,
                    borderRadius: "14px",
                    padding: isMobile ? "1.25rem" : "1.5rem",
                    position: "relative",
                  }}
                >
                  {col.label && (
                    <div style={{
                      position: "absolute", top: "-0.75rem", left: "1.25rem",
                      background: col.labelBg || "#6b7280", color: "#fff",
                      fontSize: "0.7rem", fontWeight: 700, padding: "0.2rem 0.75rem",
                      borderRadius: "999px", textTransform: "uppercase", letterSpacing: "0.04em",
                    }}>
                      {col.label}
                    </div>
                  )}
                  {col.steps.map((step, si) => (
                    <React.Fragment key={si}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", padding: "0.5rem 0" }}>
                        <span style={{ fontSize: "1.5rem", lineHeight: 1, flexShrink: 0, marginTop: "0.1rem" }}>{step.icon}</span>
                        <div>
                          <p style={{ margin: 0, fontSize: "0.92rem", fontWeight: 600, color: "#1f2937", lineHeight: 1.3 }}>{step.title}</p>
                          {step.detail && <p style={{ margin: "0.2rem 0 0 0", fontSize: "0.82rem", color: "#6b7280", lineHeight: 1.5 }}>{step.detail}</p>}
                        </div>
                      </div>
                      {si < col.steps.length - 1 && (
                        <div style={{ textAlign: "center", fontSize: "1rem", color: col.arrowColor || "#d1d5db", padding: "0.15rem 0", marginLeft: "0.5rem" }}>↓</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
            {block.caption && (
              <p style={{ textAlign: "center", fontSize: "0.85rem", color: "#6b7280", fontStyle: "italic", marginTop: "0.75rem", lineHeight: 1.5 }}>
                {block.caption}
              </p>
            )}
          </div>
        );
      case "databaseSlider":
        return <DatabaseValueSlider key={i} slug={slug} {...block} />;
      case "suburbThroughputExplorer":
        return <SuburbThroughputExplorer key={i} slug={slug} {...block} />;
      case "productMockup": {
        const def = PRODUCT_MOCKUPS[block.mockup];
        if (!def) return null;
        return (
          <figure key={i} style={{ margin: "2rem 0", textAlign: "center" }}>
            <BrowserWindow url={block.url || def.url} dark={block.dark}>
              {def.render()}
            </BrowserWindow>
            {block.caption && (
              <figcaption style={{ marginTop: "0.75rem", fontSize: "0.85rem", color: "#6b7280", fontStyle: "italic", lineHeight: 1.5 }}>
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      }
      case "dunbarCircles": {
        const layers = [
          { count: '5,000+', label: 'your CRM',           r: 250, fill: '#fee2e2', stroke: '#fecaca', text: '#991b1b' },
          { count: '1,500',  label: 'recognise by face',  r: 205, fill: '#fde68a', stroke: '#fcd34d', text: '#92400e' },
          { count: '500',    label: 'acquaintances',      r: 165, fill: '#fef3c7', stroke: '#fde68a', text: '#78350f' },
          { count: '150',    label: "Dunbar's number",    r: 125, fill: '#bbf7d0', stroke: '#86efac', text: '#14532d' },
          { count: '50',     label: 'friends',            r: 88,  fill: '#86efac', stroke: '#4ade80', text: '#14532d' },
          { count: '15',     label: 'close friends',      r: 55,  fill: '#4ade80', stroke: '#22c55e', text: '#052e16' },
          { count: '5',      label: 'inner circle',       r: 26,  fill: '#16a34a', stroke: '#15803d', text: '#ffffff' },
        ];
        const W = 720, H = 560, cx = 280, cy = H / 2;
        const labelX = 555;
        return (
          <figure key={i} style={{ margin: "2.5rem 0", textAlign: "center" }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", maxWidth: "720px", display: "block", margin: "0 auto" }} role="img" aria-label="Dunbar's number concentric circles">
              {layers.map((l, idx) => (
                <circle key={idx} cx={cx} cy={cy} r={l.r} fill={l.fill} stroke={l.stroke} strokeWidth="1.5" />
              ))}
              <text x={cx} y={cy + 4} textAnchor="middle" fontSize="14" fontWeight="700" fill="#0f172a">YOU</text>
              {layers.map((l, idx) => {
                const yPos = cy - l.r + 4;
                const anchorX = cx + l.r * 0.05;
                return (
                  <g key={`lbl-${idx}`}>
                    <line x1={anchorX} y1={yPos - 4} x2={labelX - 8} y2={yPos - 4} stroke="#cbd5e1" strokeWidth="1" />
                    <text x={labelX} y={yPos} fontSize="13" fontWeight="700" fill={l.text === '#ffffff' ? '#052e16' : l.text}>
                      {l.count}
                      <tspan fill="#475569" fontWeight="500">{`  ${l.label}`}</tspan>
                    </text>
                  </g>
                );
              })}
            </svg>
            {block.caption && (
              <figcaption style={{ marginTop: "0.75rem", fontSize: "0.9rem", color: "#6b7280", fontStyle: "italic", lineHeight: 1.5 }}>
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", background: "#fff", minHeight: "100vh" }}>
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* Hero */}
      <header
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #134e4a 100%)",
          padding: isMobile ? "5rem 1rem 3rem" : "7rem 2rem 4rem",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "780px", margin: "0 auto" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{article.heroEmoji}</div>
          <h1
            style={{
              fontSize: isMobile ? "1.75rem" : "2.5rem",
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: "1rem",
              letterSpacing: "-0.02em",
            }}
          >
            {article.title}
          </h1>
          <p style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.7)", marginBottom: "1.5rem" }}>
            {article.subtitle}
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1rem", fontSize: "0.85rem", color: "rgba(255,255,255,0.5)" }}>
            {article.author && (
              <>
                <span>{article.author}</span>
                <span>·</span>
              </>
            )}
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
          {article.tags && article.tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0.5rem", marginTop: "1.25rem" }}>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    padding: "0.2rem 0.75rem",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.85)",
                    background: "rgba(255,255,255,0.12)",
                    borderRadius: "999px",
                    border: "1px solid rgba(255,255,255,0.15)",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Article body */}
      <main
        ref={articleRef}
        style={{
          maxWidth: "720px",
          margin: "0 auto",
          padding: isMobile ? "2rem 1.25rem 3rem" : "3rem 2rem 4rem",
        }}
      >
        {article.content.map(renderBlock)}
      </main>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}
    </div>
  );
}
